import React, { useMemo } from "react";
import { Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

// ---- Color Config ----

const CHURN_PRODUCT_COLORS: Record<string, { accent: string; bg: string }> = {
  MDE: { accent: "#107C10", bg: "#ECF6EA" },
  MDO: { accent: "#107C10", bg: "#ECF6EA" },
  MDI: { accent: "#5C2D91", bg: "#F3EEF8" },
  MDA: { accent: "#C15A00", bg: "#FFF4E5" },
};

const MDC_PLAN_COLORS: Record<string, { accent: string; bg: string }> = {
  Servers:    { accent: "#107C10", bg: "#ECF6EA" },
  DCSPM:      { accent: "#1252A3", bg: "#EAF3FB" },
  Storage:    { accent: "#C15A00", bg: "#FFF4E5" },
  Containers: { accent: "#5C2D91", bg: "#F3EEF8" },
};

const MDC_PLAN_ORDER = ["Servers", "DCSPM", "Storage", "Containers"];

// ---- Helpers ----

const toText = (v: string | number): string => String(v ?? "").trim();

const parseNumber = (v: string | number | undefined): number | undefined => {
  if (v === undefined) return undefined;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
};

const formatDecimal = (v: number | undefined, digits = 1): string =>
  v === undefined
    ? "-"
    : v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });

const formatSign = (v: number): string =>
  v >= 0 ? `+${formatDecimal(v, 1)}` : formatDecimal(v, 1);

const scoreColor = (score: number | undefined): string => {
  if (score === undefined) return "#605E5C";
  if (score >= 75) return "#107C10";
  if (score >= 50) return "#C15A00";
  return "#A4262C";
};

// ---- Data Types ----

interface ChurnEntityData {
  name: string;
  isInformational: boolean;
  isOverall: boolean;
  score?: number;
  clipFormula: string;
  cumulative?: number;
  monthDeltas: number[];
}

// ---- Parser ----

const KNOWN_LABEL_PREFIXES = [
  "clip(",
  "cumulative (neg only)",
  "current attach rate",
  "current overall attach rate",
  "previous month",
  "+ve =",
  "formula:",
  "churn health score",
  "churn (informational)",
];

const isKnownLabel = (lower: string): boolean =>
  KNOWN_LABEL_PREFIXES.some((p) => lower.startsWith(p) || lower === p);

const buildChurnData = (rows: RawIndicatorRow[]): {
  entities: ChurnEntityData[];
  scoringInfoLines: string[];
  formulaText: string;
} => {
  const entityMap = new Map<string, ChurnEntityData>();
  const scoringInfoLines: string[] = [];
  let formulaText = "";

  const getOrCreate = (name: string): ChurnEntityData => {
    if (!entityMap.has(name)) {
      entityMap.set(name, {
        name,
        isInformational: false,
        isOverall: name.toLowerCase().startsWith("overall"),
        score: undefined,
        clipFormula: "",
        cumulative: undefined,
        monthDeltas: [],
      });
    }
    return entityMap.get(name)!;
  };

  for (const row of rows) {
    const section    = (row.sectionName  || "").trim();
    const metricType = (row.metricName   || "").trim();
    const label      = (row.factorLabel  || "").trim();
    const lower      = label.toLowerCase();
    const val        = parseNumber(row.value);

    // Scoring Reference section
    if (section === "Scoring Reference") {
      if (lower.startsWith("formula:")) {
        formulaText = label.replace(/^formula:\s*/i, "").trim();
      } else if (toText(row.value).toLowerCase() !== "info") {
        scoringInfoLines.push(label);
      }
      continue;
    }

    const entity = getOrCreate(section);

    if (lower === "churn health score") {
      entity.score = val;
    } else if (lower === "churn (informational)") {
      entity.score = val;
      entity.isInformational = true;
    } else if (lower.startsWith("clip(")) {
      entity.clipFormula = label;
      if (entity.score === undefined) entity.score = val;
    } else if (lower.startsWith("cumulative (neg only)")) {
      // Value is embedded in label as "Cumulative (neg only): X" — also stored as row.value
      entity.cumulative = val !== undefined ? val : parseNumber(label.split(":").pop()?.trim());
    } else if (!isKnownLabel(lower) && val !== undefined && metricType !== "Reference") {
      // Unrecognised numeric row → treat as monthly delta
      entity.monthDeltas.push(val);
    }
  }

  // Sort: plan order first, overall last
  const entities = Array.from(entityMap.values()).sort((a, b) => {
    if (a.isOverall) return 1;
    if (b.isOverall) return -1;
    const ai = MDC_PLAN_ORDER.indexOf(a.name);
    const bi = MDC_PLAN_ORDER.indexOf(b.name);
    if (ai !== -1 && bi !== -1) return ai - bi;
    return 0;
  });

  return { entities, scoringInfoLines, formulaText };
};

// ---- Month Box ----

const MonthBox = ({ value }: { value: number }) => {
  const pos = value >= 0;
  return (
    <Stack
      styles={{
        root: {
          backgroundColor: pos ? "#DFF6DD" : "#FDE7E9",
          border: `1px solid ${pos ? "#BAE0B9" : "#F4ABAB"}`,
          borderRadius: 4,
          padding: "4px 12px",
          minWidth: 48,
          alignItems: "center",
          flexShrink: 0,
        },
      }}
    >
      <Text variant="small" styles={{ root: { fontWeight: 700, color: pos ? "#107C10" : "#A4262C" } }}>
        {formatSign(value)}
      </Text>
    </Stack>
  );
};

// ---- Shared Entity Card ----

interface ChurnEntityCardProps {
  entity: ChurnEntityData;
  accentColor: string;
  bg?: string;
  minWidth?: number;
  maxWidth?: number;
}

const ChurnEntityCard: React.FC<ChurnEntityCardProps> = ({
  entity, accentColor, bg = "#FFFFFF", minWidth = 220, maxWidth = 300,
}) => {
  const sc = scoreColor(entity.score);
  const hasMonths = entity.monthDeltas.length > 0;
  const cumIsNeg = entity.cumulative !== undefined && entity.cumulative < 0;

  return (
    <Stack
      tokens={{ childrenGap: 10 }}
      styles={{
        root: {
          flex: "0 0 auto",
          minWidth,
          maxWidth,
          backgroundColor: bg,
          borderRadius: 8,
          padding: "14px 16px",
          border: "1px solid #EDEBE9",
          borderTop: `3px solid ${accentColor}`,
        },
      }}
    >
      {/* Title */}
      <Stack tokens={{ childrenGap: 2 }}>
        <Text variant="large" styles={{ root: { fontWeight: 700, color: accentColor } }}>
          {entity.name}
        </Text>
        <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
          {entity.isInformational ? "Churn (Informational)" : "Churn Health Score"}
        </Text>
      </Stack>

      {/* Score + CLIP */}
      <Stack tokens={{ childrenGap: 2 }}>
        <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: sc, lineHeight: "1" } }}>
          {formatDecimal(entity.score, 1)}
        </Text>
        {entity.clipFormula && (
          <Text variant="tiny" styles={{ root: { fontFamily: "monospace", color: "#605E5C" } }}>
            {entity.clipFormula}
          </Text>
        )}
      </Stack>

      <div style={{ height: 1, backgroundColor: "#EDEBE9" }} />

      {/* Rolling 4-Month Window */}
      {hasMonths && (
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
            Rolling 4-Month Window
          </Text>
          <Stack horizontal tokens={{ childrenGap: 6 }}>
            {entity.monthDeltas.map((v, i) => <MonthBox key={i} value={v} />)}
          </Stack>
        </Stack>
      )}

      {/* Cumulative */}
      {entity.cumulative !== undefined && (
        <Text variant="small" styles={{ root: { fontWeight: 700, color: cumIsNeg ? "#A4262C" : "#107C10" } }}>
          Cumulative: {formatDecimal(entity.cumulative, 1)}&nbsp;
          <span style={{ fontWeight: 400, color: "#605E5C" }}>
            ({cumIsNeg ? "neg only" : "no negatives"})
          </span>
        </Text>
      )}
    </Stack>
  );
};

// ---- Overall MDC Card ----

const OverallMDCCard: React.FC<{ entity: ChurnEntityData }> = ({ entity }) => {
  const sc = scoreColor(entity.score);
  const hasMonths = entity.monthDeltas.length > 0;
  const cumIsNeg = entity.cumulative !== undefined && entity.cumulative < 0;

  return (
    <Stack
      tokens={{ childrenGap: 10 }}
      styles={{
        root: {
          flex: "0 0 auto",
          minWidth: 240,
          maxWidth: 320,
          backgroundColor: "#FAFAFA",
          borderRadius: 8,
          padding: "14px 16px",
          border: "2px dashed #605E5C",
        },
      }}
    >
      {/* Title + badge */}
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text variant="large" styles={{ root: { fontWeight: 700 } }}>Overall</Text>
        <Stack
          styles={{
            root: {
              backgroundColor: "#FFF4CE",
              border: "1px solid #F2C94C",
              borderRadius: 4,
              padding: "2px 8px",
            },
          }}
        >
          <Text variant="tiny" styles={{ root: { color: "#7A5A00", fontWeight: 700 } }}>
            → feeds Health Score
          </Text>
        </Stack>
      </Stack>

      <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
        Churn Health Score (AdjustedChurnRate4Months)
      </Text>

      {/* Score + CLIP */}
      <Stack tokens={{ childrenGap: 2 }}>
        <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: sc, lineHeight: "1" } }}>
          {formatDecimal(entity.score, 1)}
        </Text>
        {entity.clipFormula && (
          <Text variant="tiny" styles={{ root: { fontFamily: "monospace", color: "#605E5C" } }}>
            {entity.clipFormula}
          </Text>
        )}
      </Stack>

      <div style={{ height: 1, backgroundColor: "#EDEBE9" }} />

      {hasMonths && (
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="small" styles={{ root: { fontWeight: 600 } }}>Rolling 4-Month Window</Text>
          <Stack horizontal tokens={{ childrenGap: 6 }}>
            {entity.monthDeltas.map((v, i) => <MonthBox key={i} value={v} />)}
          </Stack>
        </Stack>
      )}

      {entity.cumulative !== undefined && (
        <Text variant="small" styles={{ root: { fontWeight: 700, color: cumIsNeg ? "#A4262C" : "#107C10" } }}>
          Cumulative: {formatDecimal(entity.cumulative, 1)}&nbsp;
          <span style={{ fontWeight: 400, color: "#605E5C" }}>
            ({cumIsNeg ? "neg only" : "no negatives"})
          </span>
        </Text>
      )}
    </Stack>
  );
};

// ---- Main Component ----

interface MTPChurnRawIndicatorCardsProps {
  rows: RawIndicatorRow[];
  productAbbr: string;
  weight?: string;
  /** Pass true on the last product to render the shared How-to-Read / Note footer */
  showFooter?: boolean;
}

const MTPChurnRawIndicatorCards: React.FC<MTPChurnRawIndicatorCardsProps> = ({
  rows, productAbbr, weight, showFooter = false,
}) => {
  const { entities, scoringInfoLines, formulaText } = useMemo(
    () => buildChurnData(rows),
    [rows]
  );

  if (productAbbr === "MDC") {
    const planEntities = entities.filter((e) => !e.isOverall);
    const overallEntity = entities.find((e) => e.isOverall);

    return (
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="large" styles={{ root: { fontWeight: 700, color: "#323130" } }}>
          MDC Churn — Per Protection Plan
        </Text>

        {/* 4 plan cards */}
        <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
          {planEntities.map((entity) => {
            const c = MDC_PLAN_COLORS[entity.name] ?? { accent: "#323130", bg: "#FAFAFA" };
            return (
              <ChurnEntityCard key={entity.name} entity={entity} accentColor={c.accent} bg={c.bg} />
            );
          })}
        </Stack>

        {/* Overall card */}
        {overallEntity && <OverallMDCCard entity={overallEntity} />}

        {/* How to read */}
        {(scoringInfoLines.length > 0 || formulaText) && (
          <Stack
            tokens={{ childrenGap: 6 }}
            styles={{
              root: {
                backgroundColor: "#F5F5F5",
                border: "1px solid #EDEBE9",
                borderRadius: 8,
                padding: "10px 14px",
              },
            }}
          >
            <Text variant="small" styles={{ root: { fontWeight: 700 } }}>How to read:</Text>
            {scoringInfoLines.map((line, i) => (
              <Text key={i} variant="small" styles={{ root: { color: "#605E5C" } }}>{line}</Text>
            ))}
            {formulaText && (
              <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
                <strong>Churn Health Score</strong> = {formulaText}
              </Text>
            )}
          </Stack>
        )}

        {/* Note */}
        <Stack
          tokens={{ childrenGap: 4 }}
          styles={{
            root: {
              backgroundColor: "#EFF3FB",
              border: "1px solid #B8C8E8",
              borderLeft: "4px solid #1252A3",
              borderRadius: 6,
              padding: "10px 14px",
            },
          }}
        >
          <Text variant="small" styles={{ root: { fontWeight: 700 } }}>📌 Note:</Text>
          <Text variant="small">
            For <strong>Defenders</strong> (MDE/MDI/MDO/MDA), each product gets its own Churn Health Score based on its DeploymentScore (MPU/PAU).
          </Text>
          <Text variant="small">
            For <strong>MDC</strong>, only the <code>Defender_Overall_Attach</code> column feeds into the health score (per-plan churn is informational only).
          </Text>
          <Text variant="small">
            <strong>Sentinel</strong> does not have a churn indicator (structurally excluded).
          </Text>
        </Stack>
      </Stack>
    );
  }

  // Simple product card (MDE / MDO / MDI / MDA)
  const c = CHURN_PRODUCT_COLORS[productAbbr] ?? { accent: "#323130", bg: "#FFFFFF" };
  const entity =
    entities.find((e) => e.name === productAbbr) ??
    entities.find((e) => !e.isOverall) ??
    entities[0];

  if (!entity) return null;

  return <ChurnEntityCard entity={entity} accentColor={c.accent} bg={c.bg} />;
};

export default MTPChurnRawIndicatorCards;
